import * as React from "react";
import type {RenderElementProps, RenderLeafProps} from "slate-react";

import GuideCursor from "./guide-cursor.tsx";
import BlockQuoteNode from "./slate/renderers/block-quote-node.tsx";
import CodeBlockNode from "./slate/renderers/code-block-node.tsx";
import CodeInlineNode from "./slate/renderers/code-inline-node.tsx";
import HeadingNode from "./slate/renderers/heading-node.tsx";
import HorizontalRuleNode from "./slate/renderers/horizontal-rule-node.tsx";
import ImageNode from "./slate/renderers/image-node.tsx";
import LinkNode from "./slate/renderers/link-node.tsx";
import ListItemNode from "./slate/renderers/list-item-node.tsx";
import ListItemTextNode from "./slate/renderers/list-item-text-node.tsx";
import ListNode from "./slate/renderers/list-node.tsx";
import MathBlockNode from "./slate/renderers/math-block-node.tsx";
import MathInlineNode from "./slate/renderers/math-inline-node.tsx";
import ParagraphNode from "./slate/renderers/paragraph-node.tsx";
import SkeletonBlockNode from "./slate/renderers/skeleton-block-node.tsx";
import TableBodyNode from "./slate/renderers/table-body-node.tsx";
import TableCellNode from "./slate/renderers/table-cell-node.tsx";
import TableHeaderNode from "./slate/renderers/table-header-node.tsx";
import TableNode from "./slate/renderers/table-node.tsx";
import TableRowNode from "./slate/renderers/table-row-node.tsx";

export const renderElement = (
    props: RenderElementProps,
): React.ReactElement => {
    switch (props.element.type) {
        case "heading-one":
            return (
                <HeadingNode headingType="heading-one" elementProps={props} />
            );
        case "heading-two":
            return (
                <HeadingNode headingType="heading-two" elementProps={props} />
            );
        case "heading-three":
            return (
                <HeadingNode headingType="heading-three" elementProps={props} />
            );
        case "horizontal-rule":
            return <HorizontalRuleNode {...props} />;
        case "bulleted-list":
            return <ListNode listType="bulleted-list" elementProps={props} />;
        case "numbered-list":
            return <ListNode listType="numbered-list" elementProps={props} />;
        case "lettered-list":
            return <ListNode listType="lettered-list" elementProps={props} />;
        case "list-item":
            return <ListItemNode {...props} />;
        case "list-item-text":
            return <ListItemTextNode {...props} />;
        case "block-quote":
            return <BlockQuoteNode {...props} />;
        case "image":
            return <ImageNode {...props} />;
        case "link":
            return <LinkNode {...props} />;
        // case "math-editor":
        // NOTE(reid): I'm replacing MathEditor with a simpler TeX
        // renderer because the MathEditor doesn't support the full range
        // of TeX that might be rendered in the coeditor - namely, TeX
        // that uses colors and other formatting won't be rendered at all.
        // If we can overcome or workaround this issue, we could maybe
        // bring back the MathEditor here!
        //
        // return (
        //     <MathEditor
        //         element={props.element}
        //         children={props.children}
        //         attributes={props.attributes}
        //         canSendMessage={false}
        //     />
        // );
        case "math-inline":
            return <MathInlineNode {...props} />;
        case "math-block":
            return <MathBlockNode {...props} />;
        case "code-inline":
            return <CodeInlineNode {...props} />;
        case "code-block":
            return <CodeBlockNode {...props} />;
        case "table":
            return <TableNode {...props} />;
        case "table-header":
            return <TableHeaderNode {...props} />;
        case "table-body":
            return <TableBodyNode {...props} />;
        case "table-row":
            return <TableRowNode {...props} />;
        case "table-cell":
            return <TableCellNode {...props} />;
        case "skeleton-block":
            return <SkeletonBlockNode {...props} />;
        default:
            return <ParagraphNode {...props} />;
    }
};

export const renderLeaf = (props: RenderLeafProps): React.ReactElement => {
    const {leaf, attributes} = props;
    let {children} = props;

    if (leaf.bold) {
        children = <strong>{children}</strong>;
    }

    if (leaf.italic) {
        children = <em>{children}</em>;
    }

    if (leaf.underline) {
        children = <u>{children}</u>;
    }

    if (leaf.guideCursor) {
        const {atEnd = false} = leaf.guideCursor;
        children = <GuideCursor atEnd={atEnd}>{children}</GuideCursor>;
    }

    return <span {...attributes}>{children}</span>;
};
